import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, Package } from 'lucide-react';
import { formatPrice } from '@/lib/utils';

export interface QuantityTier {
  minQuantity: string;
  maxQuantity: string;
  price: string;
}

interface QuantityPricingCarouselProps {
  tiers: QuantityTier[];
  basePrice: number;
  selectedQuantity: number;
}

export function QuantityPricingCarousel({
  tiers,
  basePrice,
  selectedQuantity,
}: QuantityPricingCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const sortedTiers = [...tiers].sort((a, b) => parseInt(a.minQuantity) - parseInt(b.minQuantity));

  useEffect(() => {
    const matchIndex = sortedTiers.findIndex(
      (tier) =>
        selectedQuantity >= parseInt(tier.minQuantity) &&
        selectedQuantity <= parseInt(tier.maxQuantity)
    );
    if (matchIndex !== -1) {
      setActiveIndex(matchIndex);
    } else if (selectedQuantity > 0) {
      const lastTier = sortedTiers[sortedTiers.length - 1];
      if (selectedQuantity >= parseInt(lastTier.minQuantity)) {
        setActiveIndex(sortedTiers.length - 1);
      }
    }
  }, [selectedQuantity, sortedTiers]);

  useEffect(() => {
    if (scrollRef.current) {
      const activeCard = scrollRef.current.children[activeIndex] as HTMLElement;
      if (activeCard) {
        activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeIndex]);

  return (
    <div className="w-full">
      {/* Title */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-sm">
          <Package className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-sm font-bold text-gray-900">Wholesale Pricing</span>
      </div>

      {/* Cards */}
      <div className="overflow-hidden">
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {sortedTiers.map((tier, index) => {
            const tierPrice = parseFloat(tier.price);
            const tierSavings = basePrice - tierPrice;
            const tierSavingsPercent = tierSavings > 0 ? Math.round((tierSavings / basePrice) * 100) : 0;
            const isActive = index === activeIndex;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.08, duration: 0.3 }}
                className={`flex-shrink-0 w-28 sm:w-32 rounded-xl p-3 snap-center transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-200 ring-2 ring-brand-400 ring-offset-1'
                    : 'bg-gray-50 text-gray-700 border border-gray-200'
                }`}
              >
                {/* Quantity */}
                <div className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-gray-800'}`}>
                  {tier.minQuantity}-{tier.maxQuantity} <span className={`text-xs ${isActive ? 'text-brand-100' : 'text-gray-500'}`}>pcs</span>
                </div>

                {/* Price */}
                <div className={`text-lg sm:text-xl font-bold mt-1 ${isActive ? 'text-white' : 'text-gray-900'}`}>
                  {formatPrice(tierPrice)}
                </div>

                {/* Savings */}
                {tierSavings > 0 && (
                  <div className={`flex items-center gap-1 mt-1 text-xs font-semibold ${
                    isActive ? 'text-brand-100' : 'text-brand-600'
                  }`}>
                    <TrendingDown className="w-3 h-3" />
                    {tierSavingsPercent}% off
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-1 mt-2">
          {sortedTiers.map((_, index) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                index === activeIndex ? 'bg-brand-500 w-4' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
